import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaWhatsapp } from 'react-icons/fa';
import { ChatBubbleLeftRightIcon, XMarkIcon } from '@heroicons/react/24/outline';

const channels = [
  {
    key: 'whatsapp',
    label: 'WhatsApp',
    hint: 'Chat with us on WhatsApp',
    bg: '#25D366',
    Icon: FaWhatsapp,
  },
  {
    key: 'agent',
    label: 'Live Agent',
    hint: 'Talk to a real human',
    bg: 'var(--orange-9)',
    Icon: ChatBubbleLeftRightIcon,
  },
];

const SupportWidget = ({ mobileRaised = false }) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const handleKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  return (
    <div
      ref={rootRef}
      className={`support-widget fixed z-50 flex flex-col items-end gap-3${mobileRaised ? ' support-widget--mobile-raised' : ''}`}
      style={{ fontFamily: "'Poppins', sans-serif" }}
    >
      <AnimatePresence>
        {open && (
          <motion.div
            key="panel"
            initial={{ opacity: 0, y: 12, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.96 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
            className="flex flex-col items-end gap-2"
            style={{
              width: '280px',
              backgroundColor: '#fff',
              borderRadius: '16px',
              padding: '14px',
              boxShadow: '0 18px 50px rgba(0,0,0,0.15), 0 2px 6px rgba(0,0,0,0.06)',
              border: '1px solid rgba(0,0,0,0.05)',
            }}
          >
            <div className="w-full flex items-start justify-between mb-1">
              <div>
                <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--gray-12)' }}>
                  Need help?
                </div>
                <div style={{ fontSize: '12px', color: 'var(--gray-10)' }}>
                  Pick a channel below
                </div>
              </div>
            </div>

            {channels.map(({ key, label, hint, bg, Icon }, idx) => (
              <motion.button
                key={key}
                type="button"
                disabled
                initial={{ opacity: 0, x: 8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.04 + idx * 0.05, duration: 0.18 }}
                className="w-full flex items-center gap-3 p-2.5 rounded-xl text-left transition-colors cursor-not-allowed"
                style={{ backgroundColor: 'var(--gray-2)' }}
                title={`${label} — coming soon`}
              >
                <span
                  style={{
                    width: '38px',
                    height: '38px',
                    borderRadius: '50%',
                    backgroundColor: bg,
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <Icon style={{ width: '20px', height: '20px' }} />
                </span>
                <span className="flex-1 min-w-0">
                  <span
                    style={{
                      display: 'block',
                      fontSize: '13px',
                      fontWeight: 600,
                      color: 'var(--gray-12)',
                    }}
                  >
                    {label}
                  </span>
                  <span
                    style={{
                      display: 'block',
                      fontSize: '11px',
                      color: 'var(--gray-10)',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {hint}
                  </span>
                </span>
                <span
                  style={{
                    fontSize: '10px',
                    fontWeight: 700,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    padding: '3px 8px',
                    borderRadius: '999px',
                    backgroundColor: 'var(--orange-3)',
                    color: 'var(--orange-11)',
                    flexShrink: 0,
                  }}
                >
                  Soon
                </span>
              </motion.button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        type="button"
        onClick={() => setOpen((v) => !v)}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        aria-label={open ? 'Close support' : 'Open support'}
        className="relative flex items-center justify-center rounded-full"
        style={{
          width: 'clamp(48px, 14vw, 56px)',
          height: 'clamp(48px, 14vw, 56px)',
          backgroundColor: 'var(--orange-9)',
          color: '#fff',
          boxShadow: '0 10px 30px rgba(240,90,53,0.45), 0 2px 6px rgba(0,0,0,0.08)',
          cursor: 'pointer',
          border: 'none',
        }}
      >
        {!open && (
          <span
            aria-hidden
            style={{
              position: 'absolute',
              inset: 0,
              borderRadius: '50%',
              backgroundColor: 'var(--orange-9)',
              opacity: 0.35,
              animation: 'supportPulse 1.8s ease-out infinite',
            }}
          />
        )}
        <AnimatePresence mode="wait" initial={false}>
          {open ? (
            <motion.span
              key="close"
              initial={{ rotate: -90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 90, opacity: 0 }}
              transition={{ duration: 0.15 }}
              style={{ display: 'flex', zIndex: 1 }}
            >
              <XMarkIcon style={{ width: '24px', height: '24px' }} />
            </motion.span>
          ) : (
            <motion.span
              key="chat"
              initial={{ rotate: 90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: -90, opacity: 0 }}
              transition={{ duration: 0.15 }}
              style={{ display: 'flex', zIndex: 1 }}
            >
              <ChatBubbleLeftRightIcon style={{ width: '24px', height: '24px' }} />
            </motion.span>
          )}
        </AnimatePresence>
      </motion.button>

      <style>{`
        .support-widget {
          right: 14px;
          bottom: max(14px, env(safe-area-inset-bottom));
        }

        @media (max-width: 767px) {
          .support-widget--mobile-raised {
            bottom: calc(74px + max(12px, env(safe-area-inset-bottom)));
          }
        }

        @keyframes supportPulse {
          0%   { transform: scale(1);   opacity: 0.35; }
          70%  { transform: scale(1.6); opacity: 0;    }
          100% { transform: scale(1.6); opacity: 0;    }
        }
      `}</style>
    </div>
  );
};

export default SupportWidget;
